import React, { Fragment, useState } from 'react'

import {
  faCopy,
  faEdit,
  faEllipsisV,
  faMinusCircle,
  faPlusCircle,
  faSkullCrossbones,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconButton, Menu, MenuItem, SvgIcon } from '@material-ui/core'
import { styled } from '@material-ui/core/styles'
import { noop } from 'lodash'

import useIsShadow from 'common/use-is-shadow'
import { useOpenModalForCopy, useOpenModalForEdit } from 'components/ComposeOutreachMessageButton/Context'
import { ONE_TIME_TRIGGER } from 'components/SegmentSelector/outreach-message-trigger-constants'

const Icon = styled(FontAwesomeIcon)({
  marginRight: 6,
})

type ActionsProps = {
  record: {
    Id: string
    Trigger: string
    IsDraft?: boolean
    Disabled?: boolean
    IsStarter?: boolean
    Status?: string
  }
  onDelete: () => void
  onEnable: () => void
  onDisable: () => void
  canModifyMessages: boolean
}

const Actions = ({ record, onDelete, onEnable, onDisable, canModifyMessages }: ActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isShadow = useIsShadow()
  const openEditModal = useOpenModalForEdit()
  const openCopyModal = useOpenModalForCopy()

  const id = `${record.Id}-action-menu`

  const onEdit = () => openEditModal(record)
  const onCopy = () => openCopyModal(record)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleCloseWrapper =
    (fn = noop) =>
    () => {
      setAnchorEl(null)
      fn()
    }

  const canEdit = record.IsDraft || record.Trigger !== ONE_TIME_TRIGGER || record.Status === 'Scheduled'
  const canAdminEdit = !canEdit && record.Trigger === ONE_TIME_TRIGGER && isShadow
  const canEnable = record.Trigger !== ONE_TIME_TRIGGER && record.Disabled === true
  const canDisable =
    record.Trigger !== ONE_TIME_TRIGGER &&
    (record.Disabled === undefined || record.Disabled === false) &&
    !record.IsDraft
  return (
    <Fragment>
      <IconButton
        aria-label="open actions menu"
        aria-controls={id}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <SvgIcon fontSize="small">
          <FontAwesomeIcon icon={faEllipsisV} />
        </SvgIcon>
      </IconButton>
      <Menu
        id={id}
        variant="menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseWrapper()}
      >
        {canEdit && (
          <MenuItem
            onClick={handleCloseWrapper(onEdit)}
            disabled={!canModifyMessages}
          >
            <Icon icon={faEdit} />
            Edit
          </MenuItem>
        )}
        {canAdminEdit && (
          <MenuItem onClick={handleCloseWrapper(onEdit)}>
            <Icon icon={faSkullCrossbones} />
            Admin Edit
          </MenuItem>
        )}
        {canEnable && (
          <MenuItem
            onClick={handleCloseWrapper(onEnable)}
            disabled={!canModifyMessages && !record.IsStarter}
          >
            <Icon icon={faPlusCircle} />
            Enable
          </MenuItem>
        )}
        {canDisable && (
          <MenuItem
            onClick={handleCloseWrapper(onDisable)}
            disabled={!canModifyMessages && !record.IsStarter}
          >
            <Icon icon={faMinusCircle} />
            Disable
          </MenuItem>
        )}
        <MenuItem
          onClick={handleCloseWrapper(onDelete)}
          disabled={!canModifyMessages}
        >
          <Icon icon={faTrash} />
          Delete
        </MenuItem>
        <MenuItem
          onClick={handleCloseWrapper(onCopy)}
          disabled={!canModifyMessages}
        >
          <Icon icon={faCopy} />
          Copy
        </MenuItem>
      </Menu>
    </Fragment>
  )
}

export default Actions
